<!--
 * @Description: 客户区域插件
 * @Author: cxh
 * @Date: 2022-05-07 17:55:14
 * @LastEditTime: 2023-04-14 09:38:19
 * @LastEditors: cxh
-->
<template>
  <div class="flex">
    <CosViewInput :value="checkNode" v-if="view" v-bind="$attrs" />
    <template v-else>
      <a-cascader
        :placeholder="placeholderComputed"
        v-bind="$attrs"
        :change-on-select="changeOnSelect"
        v-model:value="area"
        :fieldNames="fieldNamesComputed"
        :options="processedAreaData"
        @change="change"
        v-if="type === 'cascader' && !view"
        :class="{ 'form-item-disabled-view': formTypeComputed }"
        :showSearch="true"
      />
      <div v-else-if="type === 'text'" :title="checkNode">{{ checkNode || '--' }}</div>
    </template>
  </div>
</template>
<script lang="ts" setup>
import { onMounted, computed, inject, ref, watch, shallowRef } from 'vue'
import { useAreaStore } from '@/stores/area'
import CosViewInput from '@/components/Antd/CosView/input.vue'
import { useI18n } from 'vue-i18n'
import { useInteractionStore } from '@/stores/interaction'
import { integer, number } from 'vue-types'
const { t } = useI18n()
const areaStore = useAreaStore()
const props = defineProps({
  value: {
    type: String
  },
  defaultValue: String,
  type: {
    type: String,
    default: 'cascader' // select 可编辑form-item  text 文本
  },
  'change-on-select': {
    type: Boolean,
    default: true
  },
  view: {
    type: Boolean
  },
  placeholder: {
    type: String
  },
  onlyChina: {
    type: Boolean,
    default: false
  },
  displayLevel: {
    type: number,
    default: 0
  }
})
interface areaEum {
  path?: string
  fullName?: string
  parentId?: string
  id?: string
  name?: string
  children?: areaEum[]
}
const formTypeComputed = computed(() => {
  return inject('formItemView', props.view)
})
const placeholderComputed = computed(() => {
  if (formTypeComputed.value) {
    return '--'
  }
  return props.placeholder || t('common.pleaseSelect')
})
const area = ref<string[]>([])
const checkNode = ref<string>('')
const processedAreaData = shallowRef<any[]>([])
const dataProcessed = ref(false)

// 找到全路径
function getPath(val: string): any {
  if (!processedAreaData.value || !processedAreaData.value.length) {
    return ''
  }
  if (!val || typeof val !== 'string') {
    return ''
  }
  const v = val.split('/')
  const result: any = []
  const traverse = function (v: string[], data: any, index: number): any {
    const obj = data.find((item: any) => item.id === v[index])
    if (obj) {
      result.push(obj.name)
      if (v[index + 1] && obj.children) {
        traverse(v, obj.children, index + 1)
      }
    }
  }
  traverse(v, processedAreaData.value, 0)
  return result?.join('/')
}
const emit = defineEmits(['update:value', 'change', 'update:label'])
const shareInfo = useInteractionStore()
const eumns = {
  'en-US': 'enName',
  'zh-CN': 'cnName'
}
const fieldNamesComputed = computed(() => {
  return { label: eumns[shareInfo.language] || 'name', value: 'id', children: 'children' }
})
onMounted(() => {
  fetch()
})

// 深拷贝并处理数据，添加enName和cnName
const deepCloneAndProcess = (data: any[]): any[] => {
  return data.map(item => {
    const newItem: any = {
      id: item.id,
      name: item.name,
      parentId: item.parentId
    }

    // 添加国家名称字段
    if (item.countryEnName) newItem.countryEnName = item.countryEnName
    if (item.countryCnName) newItem.countryCnName = item.countryCnName

    // 处理名称
    if (item.parentId === '-1') {
      newItem.enName = item.countryEnName
      newItem.cnName = item.countryCnName
    } else {
      newItem.enName = item.name
      newItem.cnName = item.name
    }

    // 递归处理children
    if (item.children && item.children.length > 0) {
      newItem.children = deepCloneAndProcess(item.children)
    }

    return newItem
  })
}

// 限制区域数据的层级深度
const limitAreaLevel = (data: any[], currentLevel: number, maxLevel: number): any[] => {
  return data.map(item => {
    const newItem = { ...item }
    if (currentLevel < maxLevel && item.children && item.children.length > 0) {
      newItem.children = limitAreaLevel(item.children, currentLevel + 1, maxLevel)
    } else {
      delete newItem.children
    }
    return newItem
  })
}

const fetch = async () => {
  if (dataProcessed.value) return

  await areaStore.fetchData()

  // 使用深拷贝避免修改原始数据
  let clonedData = deepCloneAndProcess(areaStore.area)

  if (props.onlyChina) {
    const chinaData = clonedData.find((item: any) => item.id === '100000000000000000')
    clonedData = chinaData?.children || []
  }

  if (props.displayLevel > 0) {
    clonedData = limitAreaLevel(clonedData, 1, props.displayLevel)
  }

  processedAreaData.value = clonedData
  dataProcessed.value = true

  // 如果有初始值，更新checkNode
  if (props.value && (props.type === 'text' || props.view)) {
    const valNames = getPath(props.value)
    checkNode.value = valNames || props.value
  }
}

const change = (value: any, selectedOptions: areaEum[]) => {
  const resultVal = value === undefined ? '' : value
  emit('update:value', resultVal ? resultVal.join('/') : resultVal)
  const names = selectedOptions?.map((item: areaEum) => item.name)
  emit('update:label', names?.join('/'))
  emit('change', resultVal, selectedOptions)
}

watch(
  () => props.value,
  (v) => {
    if (v) {
      if (v !== area.value.join('/')) {
        area.value = v.split('/')
        if ((props.type === 'text' || props.view) && dataProcessed.value) {
          const valNames = getPath(v)
          checkNode.value = valNames || props.value
        }
      }
    } else {
      area.value = []
    }
  }
)
</script>
